import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import { useParams } from "react-router-dom";
import Topbar from "./Topbar";
import Footer from "./Footer";
import toast from "react-hot-toast";
import HoloCard from "./HoloCard";
import HoloCardInspect from "./HoloCardInspect";
import Loader from "./Loader";
import CircleProgress from "./CircleProgress";

const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);

const Collection = () => {
  const { leagueId } = useParams();
  const [collection, setCollection] = useState([]);
  const [inspectedCard, setInspectedCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOwnedSkins = async () => {
      try {
        setLoading(true);

        // Get Supabase session
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError || !session || !session.user) {
          console.error("User not logged in or session expired:", sessionError?.message);
          setLoading(false);
          await supabase.auth.signOut();
          window.location.href = "/login";
          return;
        }

        const token = session.access_token;

        // Fetch owned skins from backend
        const { data } = await axios.get(`${apiBase}/users/collection`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCollection(data.collection);
      } catch (err) {
        console.error("Failed to fetch owned skins:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOwnedSkins();
  }, [leagueId]);

  const groupedCollections = useMemo(() => {
    return collection.reduce((acc, skin) => {
      const name = skin.collection_name || "Uncategorized";
      if (!acc[name]) acc[name] = [];
      acc[name].push(skin);
      return acc;
    }, {});
  }, [collection]);

  const handleEquipSkin = async (playerId, playerSkinId) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await axios.post(`${apiBase}/api/users/equip-skin`, { playerId, playerSkinId }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(res.data.message);

      setCollection((prev) =>
        prev.map((skin) => {
          if (skin.player_id === playerId) {
            return { ...skin, equipped: skin.player_skin_id === playerSkinId };
          }
          return { ...skin, equipped: false }; // unequip other skins for same player
        }),
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to equip skin");
    }
  };

  if (loading) return <Loader loadingTitle={"Loading Collection"} showLogo={true} />;

  return (
    <div className="page-container" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div className="main">
        {!leagueId ? <Topbar showBudget={false} showLinks={false} showEmptyRewards={true} /> : <Topbar leagueId={leagueId} showBudget showLinks />}
        <div>
          <h2 style={{ color: "white", margin: "20px 0 4px 0" }}>Collections</h2>
          <p
            style={{
              margin: "0",
              color: "white",
              opacity: "0.4",
              fontSize: "13px",
              borderBottom: "1px solid rgb(25, 35, 47)",
              paddingBottom: "6px",
            }}>
            Preview and equip cards by selecting them
          </p>
          <div
            className="collections-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", // each collection block
              gap: "15px",
              marginTop: "30px",
            }}>
            {Object.entries(groupedCollections).map(([collectionName, skins]) => {
              const ownedCount = skins.filter((s) => s.owned).length;
              const totalCount = skins.length;
              const completionPercent = Math.round((ownedCount / totalCount) * 100);
              const bannerUrl = `/banners/${skins[0]?.banner_url}` || "/banners/default.png";
              const titleUrl = skins[0]?.title_url ? `/${skins[0].title_url}` : null;
              const collectionFoil = skins[0]?.collection_foil ? `/${skins[0].collection_foil}` : null;
              return (
                <div key={collectionName} className="collection-container">
                  {/* Collection header */}
                  <div
                    className="collection-header"
                    style={{
                      width: "100%",
                      height: "76px", // adjust as needed
                      borderTopRightRadius: "12px",
                      borderTopLeftRadius: "12px",
                      backgroundImage: `url(${bannerUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      display: "flex",
                      justifyContent: "center",
                      gap: "6px",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      boxSizing: "border-box",
                      position: "relative",
                      padding: "0 16px",
                      color: "white",
                      textShadow: "0 0 6px rgba(0,0,0,0.9)",
                      borderBottom: "2px solid #ffffff33",
                    }}>
                    {titleUrl ? (
                      <img src={titleUrl} alt={`${collectionName} Title`} style={{ width: "94%", objectFit: "contain" }} />
                    ) : (
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: "800",
                          textShadow: "0px 0px 8px rgba(0, 0, 0, 0.6)",
                        }}>
                        {collectionName}
                      </h4>
                    )}
                    <img src={collectionFoil} style={{ height: "42px", position: "absolute", top: "-14px", right: "-10px" }} alt="Collection Foil"/>
                    <div style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <CircleProgress percent={completionPercent} size={20} strokeWidth={6} />
                      <div>
                        <b style={{ fontSize: "16px" }}>{ownedCount}</b>/<b style={{ opacity: "0.6", fontSize: "12px" }}>{totalCount}</b>
                      </div>
                    </div>
                  </div>
                  <p
                    style={{
                      margin: "0",
                      color: "white",
                      opacity: "0.4",
                      fontSize: "12px",
                      padding: "10px 20px 0 20px",
                    }}>
                    Select a card to preview
                  </p>
                  {/* Cards grid */}
                  <div
                    className="collection-cards-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))",
                      gap: "16px",
                      padding: "10px 20px 20px 20px",
                    }}>
                    {skins.map((skin) => {
                      const isOwned = skin.owned;

                      return (
                        <div
                          key={skin.player_skin_id}
                          className={`collection-card ${isOwned ? "owned" : "missing"}`}
                          style={{ cursor: isOwned ? "pointer" : "default" }}
                          onClick={() => {
                            if (!isOwned) return;

                            setInspectedCard({
                              image: `/players/${skin.image_url}`,
                              foil: `/players/${skin.foil_url}`,
                              mask: `/players/${skin.mask_url}`,
                              topLayer: `/players/cards/${skin.top_layer}`,
                              rarity: skin.rarity,
                              playerId: skin.player_id,
                              playerSkinId: skin.player_skin_id,
                              equipped: skin.equipped,
                              showEquip: true,
                              titleUrl,
                              collectionName,
                            });
                          }}>
                          <HoloCard
                            image={`/players/${skin.image_url}`}
                            foil={`/players/${skin.foil_url}`}
                            mask={`/players/${skin.mask_url}`}
                            topLayer={`/players/cards/${skin.top_layer}`}
                            rarity={skin.rarity}
                            width={62}
                            height={101}
                            borderRadius="0px"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Footer />

      {/* Inspect modal */}
      {inspectedCard && <HoloCardInspect {...inspectedCard} onClose={() => setInspectedCard(null)} handleEquipSkin={handleEquipSkin} />}
    </div>
  );
};

export default Collection;
